import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import "@/App.css";
import EntryScreen from "./components/landing/WatercolorLanding";
import GalleryRoom from "./components/gallery/GalleryRoom";
import FallbackGrid from "./components/FallbackGrid";
import Rooms2D from "./components/Rooms2D";
import DetailsView from "./components/DetailsView";
import WoodsExperience from "./components/experiences/WoodsExperience";
import LotusExperience from "./components/experiences/LotusExperience";
import LoveExperience from "./components/experiences/LoveExperience";
import Immersion, { prefetchJourney } from "./components/immersion/Immersion";
import Credits from "./components/Credits";
import { RightsLine } from "./components/Rights";
import { Arrival } from "./components/Arrival";
import { HeartGuard } from "./components/HeartGuard";
import { LetterForm } from "./components/LetterForm";
import { HeartToggle } from "./components/HeartBloom";
import { RainbowArrival } from "./components/RainbowArrival";
import { GLBoundary } from "./components/GLBoundary";
import { MuteButton, SeeButton } from "./components/Controls";
import { BrandBanner } from "./components/BrandBanner";
import { fetchPaintings, fetchSettings, hasWebGL, imageSrc } from "./lib/api";
import { audio, MUTE_KEY } from "./lib/audio";
import { music } from "./lib/orchestra";
import { tracks } from "./lib/tracks";
import { glCrashed, hasWebGL2, mark, perf, slowDevice } from "./lib/perf";
import { diag, diagNote } from "./lib/diag";
import Hall2D from "./components/Hall2D";
import { DebugTimer } from "./components/DebugTimer";
import { POEM } from "./content/poem";
import SittingScene from "./components/bench/SittingScene";
import { SitInvite } from "./components/bench/SitInvite";
import { FlipbookOverlay } from "./components/FlipbookOverlay";
import { startPresence } from "./lib/presence";
import { BENCH_MOOD } from "./lib/songs";
import { fetchLibrary, fetchOpening } from "./lib/api";
import { OpeningLetter } from "./components/opening/OpeningLetter";
import { JourneyCard, LeaveButton } from "./components/journey/JourneyCard";
import { GalleryShare } from "./components/share/GalleryShare";
import { ReturnControl } from "./components/nav/ReturnControl";
import { BubbleVeil } from "./components/nav/BubbleVeil";
import { GatherBack } from "./components/nav/GatherBack";
import { hallView } from "./components/gallery/GalleryRoom";
import { glide, portal, pose, standBefore } from "./components/gallery/Player";
import { feelingName, RAINBOW_ORDER, SECTIONS } from "./lib/sections";
import { FEEL_STAND } from "./lib/world";

const OPENING_KEY = "afae-opening-seen";

const VIEWED_KEY = "afae-viewed";
const SAT_KEY = "afae-sat";
const readViewed = () => {
  try {
    return JSON.parse(sessionStorage.getItem(VIEWED_KEY)) || [];
  } catch {
    return [];
  }
};

const EXPERIENCES = { woods: WoodsExperience, love: LoveExperience, lotus: LotusExperience };
const MOOD_SCENE = { woods: "woods", love: "love", lotus: "pond" };
const moodOf = (p) => MOOD_SCENE[p.experience_type] || "reveal";
const abs = (u) => (u ? imageSrc(u) : null);

window.__afaeAudio = { state: () => ({ ...audio.state(), music: { ...music.state(), custom: tracks.state().custom, track: tracks.state() } }) };

function score(name, mood, url) {
  if (url) {
    music.rest(name);
    tracks.play(abs(url)).then((ok) => !ok && music.play(name, mood));
  } else {
    tracks.play(null);
    music.play(name, mood);
  }
}

const FORCE_ARRIVAL = new URLSearchParams(window.location.search).get("arrival") === "1";
const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const LIB = { tracks: [], sections: {}, hall: null, bench: null, arrival: null };
const trackUrl = (id) => LIB.tracks.find((t) => t.id === id)?.url || null;
const paintingUrl = (p) => p.music_url || trackUrl(p.track_id || LIB.sections[p.section]);
const push = (afae, url) => window.history.pushState({ afae }, "", url);

export default function App() {
  const webgl = useMemo(() => hasWebGL() && hasWebGL2(), []);
  const [paintings, setPaintings] = useState([]);
  const [settings, setSettings] = useState({ poem: POEM.lines, hall_music_url: null });
  const [libReady, setLibReady] = useState(false);
  const hallUrl = settings.hall_music_url || trackUrl(settings.hall_track_id || LIB.hall);
  const benchUrl = trackUrl(LIB.bench) || hallUrl;
  const arrivalFull = trackUrl(settings.arrival_track_id || LIB.arrival);
  const arrivalUrl = arrivalFull ? arrivalFull.replace(/\.mp3$/, "-arrival.mp3") : hallUrl;
  const [entered, setEntered] = useState(false);
  const [audioOn, setAudioOn] = useState(false);
  const [mode, setMode] = useState(webgl ? "3d" : "2d");
  // the flat views: the painted Feeling Wall first; one room at a time or the catalogue only when the visitor chooses them
  const [view2d, setView2d] = useState("wall");
  const [room2d, setRoom2d] = useState(null);
  const [galleryKey, setGalleryKey] = useState(0);
  // a 3D hall that fails is rebuilt once in its lightest form; only a second failure shows the painted 2D Feeling Wall
  const [glLite, setGlLite] = useState(glCrashed);
  const liteTried = useRef(false);
  const fallTo2D = useCallback((why, detail = "") => {
    if (!liteTried.current) {
      liteTried.current = true;
      diag(`retry-lite:${why}`, detail);
      setGlLite(true);
      setGalleryKey((k) => k + 1);
      return;
    }
    diag(`hall-2d:${why}`, detail);
    diagNote({ view: "hall-2d" });
    setView2d("wall");
    setMode("2d");
  }, []);
  useEffect(() => {
    if (webgl) return;
    diagNote({ view: "hall-2d" });
    diag("no-webgl", `webgl:${hasWebGL()} webgl2:${hasWebGL2()}`);
  }, [webgl]);
  // a lost gallery context (phones evict it while a journey holds its own) is rebuilt, not demoted to the flat view; only repeated losses fall back
  const [lostPending, setLostPending] = useState(false);
  const losses = useRef(0);
  const onGalleryFallback = useCallback(
    (why) => {
      if (why === "context-lost" && losses.current < 2) {
        losses.current += 1;
        return setLostPending(true);
      }
      fallTo2D(why);
    },
    [fallTo2D],
  );
  const [active, setActive] = useState(null);
  const [stage, setStage] = useState(null);
  // the rebuilt gallery waits until the visitor has been back in the gallery for a moment (never mid-navigation)
  useEffect(() => {
    if (!lostPending || stage || active) return;
    const id = setTimeout(() => {
      setLostPending(false);
      setGalleryKey((k) => k + 1);
    }, 2500);
    return () => clearTimeout(id);
  }, [lostPending, stage, active]);
  const [muted, setMuted] = useState(() => sessionStorage.getItem(MUTE_KEY) === "1");
  const [seated, setSeated] = useState(false);
  const [viewed, setViewed] = useState(readViewed);
  const [revealing, setRevealing] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [opening, setOpening] = useState(null);
  const [veil, setVeil] = useState(null);
  const [gather, setGather] = useState(null);
  const [arrival, setArrival] = useState(false);
  const [arrivalHold, setArrivalHold] = useState(false);
  const [galleryRegion, setGalleryRegion] = useState("hall");
  const [atMemory, setAtMemory] = useState(false);
  const [memoryWave, setMemoryWave] = useState(false);
  const [sharing, setSharing] = useState(false);
  const onMemory = useCallback((at) => {
    setAtMemory(at);
    if (!at) setMemoryWave(false);
  }, []);
  useEffect(() => {
    const share = () => setSharing(true);
    window.addEventListener("afae-share-gallery", share);
    return () => window.removeEventListener("afae-share-gallery", share);
  }, []);
  const [tourEnd, setTourEnd] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [flipbook, setFlipbook] = useState(false);
  const [nearBook, setNearBook] = useState(false);
  useEffect(() => void (window.__afaeNearBook = nearBook), [nearBook]);
  useEffect(() => {
    const on = (e) => setMenuOpen(!!e.detail);
    window.addEventListener("afae-menu", on);
    return () => window.removeEventListener("afae-menu", on);
  }, []);
  useEffect(() => {
    const on = (e) => setTourEnd(!!e.detail);
    window.addEventListener("afae-tourend", on);
    return () => window.removeEventListener("afae-tourend", on);
  }, []);
  useEffect(() => {
    const leave = () => setLeaving(true);
    window.addEventListener("afae-leave", leave);
    return () => window.removeEventListener("afae-leave", leave);
  }, []);
  const pending = useRef(null);
  const fullPreload = useRef(null);
  const [settingsReady, setSettingsReady] = useState(false);
  const unlocked = useRef(false);
  const place = !entered ? "landing" : stage ? "painting" : seated || revealing ? "bench" : "hall";
  const placeRef = useRef(place);
  placeRef.current = place;
  const regionRef = useRef(galleryRegion);
  regionRef.current = galleryRegion;
  const nav = useRef({});
  const skipPop = useRef(0);
  const unwind = () => {
    skipPop.current++;
    window.history.back();
  };

  useEffect(() => {
    if (window.location.pathname.startsWith("/p/")) return;
    fetchOpening()
      .then((o) => o.active && localStorage.getItem(OPENING_KEY) !== o.key && setOpening(o))
      .catch(() => {});
  }, []);
  useEffect(() => {
    fetchLibrary()
      .then((l) => {
        Object.assign(LIB, l);
        setLibReady(true);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (libReady && audioOn && !active && !seated && !revealing) score(arrival ? "arrival" : "hall", undefined, arrival ? arrivalUrl : hallUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libReady]);
  const closeOpening = () => {
    localStorage.setItem(OPENING_KEY, opening.key);
    setOpening(null);
  };

  useEffect(() => {
    startPresence();
    // the landing paints first; the instrument samples and painting images follow once it is up
    const pre = setTimeout(() => music.prefetch(), 6000);
    fetchPaintings()
      .then((list) => {
        setPaintings(list);
        // the landing stays light: only small thumbnails now; the full paintings follow once the visitor enters
        list.forEach((p, i) => p.thumb_url && setTimeout(() => (new Image().src = imageSrc(p.thumb_url)), 2500 + i * 60));
        fullPreload.current = () => list.forEach((p, i) => setTimeout(() => (new Image().src = imageSrc(p.image_url)), 1500 + i * 150));
      })
      .catch(() => setPaintings([]));
    fetchSettings()
      .then((s) => setSettings((old) => ({ ...old, ...s, poem: s.poem?.length ? s.poem : old.poem })))
      .catch(() => {})
      .finally(() => setSettingsReady(true));
    return () => clearTimeout(pre);
  }, []);

  useEffect(() => {
    if (libReady && !entered) tracks.preload(abs(arrivalUrl));
  }, [libReady, entered, arrivalUrl]);
  const unlockAudio = () => {
    if (unlocked.current) return;
    unlocked.current = true;
    audio.start(muted);
    music.init(audio.ctx, audio.master);
    tracks.init(audio.ctx, audio.master);
    if (!music.current && !tracks.want) score("hall", undefined, hallUrl);
    setAudioOn(true);
  };
  // while the landing listens for a breath, its music falls silent (it would flood the open mic); the arrival brings music back
  useEffect(() => {
    const quiet = () => {
      tracks.play(null, false, 0.3);
      music.rest("hall");
    };
    window.addEventListener("afae-mic-on", quiet);
    return () => window.removeEventListener("afae-mic-on", quiet);
  }, []);
  const startArrival = () => {
    const kind = FORCE_ARRIVAL || settings.rainbow_arrival !== false ? "rainbow" : "arch";
    setArrival(kind);
    setArrivalHold(kind === "rainbow");
    score("arrival", undefined, arrivalUrl);
  };
  // Every landing → gallery entry plays the arrival; only browser forward/back skips it.
  const enter = (fromHistory) => {
    // a fresh visit never inherits a glide or portal left running when the visitor last left the gallery
    glide.on = portal.on = false;
    unlockAudio();
    fullPreload.current?.();
    fullPreload.current = null;
    audio.setScene("gallery");
    if (fromHistory === true) setVeil("burst");
    else startArrival();
    setEntered(true);
    if (fromHistory !== true) push("hall", "/");
  };
  const fromWindow = useRef(false);
  // opened from a room and its journey experienced: closing the details goes straight back to that painting in the room
  const fromRoom = useRef(false);
  const journeyDone = useRef(false);
  const open = useCallback((p, room = false) => {
    // a second tap while the painting is already opening must not stack another history step
    if (stageRef.current) return;
    fromWindow.current = false;
    fromRoom.current = room;
    journeyDone.current = false;
    setActive(p);
    setStage("immersion");
    push("painting", `/p/${p.slug}`);
    audio.setScene(moodOf(p));
    score(p.slug, p.mood, paintingUrl(p));
    audio.hush();
    setViewed((v) => {
      if (v.includes(p.slug)) return v;
      const next = [...v, p.slug];
      sessionStorage.setItem(VIEWED_KEY, JSON.stringify(next));
      return next;
    });
  }, []);
  // A Living Window opens its full experience directly; returning lands back in front of that window.
  const openSpecial = useCallback((p) => {
    open(p);
    if (!EXPERIENCES[p.experience_type]) return;
    fromWindow.current = true;
    if (p.experience_type === "lotus") audio.setScene("pond");
    setStage("experience");
  }, [open]);
  const benchMusic = useCallback(() => {
    audio.setScene("bench");
    if (benchUrl) score("bench", BENCH_MOOD, benchUrl);
    else {
      tracks.play(null);
      music.play("bench", BENCH_MOOD, 1.18);
    }
  }, [benchUrl]);
  const sit = useCallback(() => {
    setRevealing(false);
    setSeated(true);
    sessionStorage.setItem(SAT_KEY, "1");
    benchMusic();
  }, [benchMusic]);
  const reveal = useCallback(() => {
    push("bench", "/");
    if (mode !== "3d") return sit();
    setRevealing(true);
    benchMusic();
  }, [mode, sit, benchMusic]);
  useEffect(() => {
    window.__afaeSit = reveal;
    return () => delete window.__afaeSit;
  }, [reveal]);
  const stand = useCallback(() => {
    fromWindow.current = false;
    fromRoom.current = false;
    journeyDone.current = false;
    setSeated(false);
    setRevealing(false);
    audio.setScene("gallery");
    score("hall", undefined, hallUrl);
  }, [hallUrl]);
  // the Memory Wall is the ending: offered only at the end of the rooms or on the way out, never mid-room
  const hasSat = () => sessionStorage.getItem(SAT_KEY) === "1";
  const toMemories = () => {
    setLeaving(false);
    if (mode === "3d" && window.__afae?.glide) window.__afae.glide("memory");
    else reveal();
  };
  const enterWith = (p) => {
    standBefore(paintings, p.slug);
    pending.current = p;
    enter();
  };
  const arrivalRef = useRef(false);
  arrivalRef.current = arrival;
  const onMusic = useCallback(
    (target, painting, sec) => {
      // the arrival waltz plays to its end; the hall's zone music takes over only after it
      if (arrivalRef.current) return;
      if (painting) return score(painting.slug, painting.mood, paintingUrl(painting));
      const lead = sec && paintings.find((q) => q.section === sec);
      if (lead) return score(target, { ...lead.mood, violin: (lead.mood?.violin || 0.1) * 0.7 }, trackUrl(LIB.sections[sec]) || hallUrl);
      score("hall", undefined, hallUrl);
    },
    [paintings, hallUrl],
  );
  const toNext = useCallback(() => {
    journeyDone.current = true;
    // every story journey ends on the details; the interactive plays open only from the hall's Living Windows
    setStage("details");
  }, []);
  const stageRef = useRef(stage);
  stageRef.current = stage;
  const toProjection = () => {
    setStage("immersion");
    if (active) audio.setScene(moodOf(active));
  };
  const toDetails = useCallback(() => setStage("details"), []);
  const resetRefs = () => {
    fromWindow.current = false;
    fromRoom.current = false;
    journeyDone.current = false;
  };
  const back = () => {
    resetRefs();
    if (active && mode === "3d") setGather({ painting: active, target: hallView.door(active.slug) });
    setActive(null);
    setStage(null);
    audio.setScene("gallery");
    if (mode === "2d") score("hall", undefined, hallUrl);
    else window.__afaeMusicResync?.();
    // leaving a painting by the UI steps the history back too, so browser Back keeps following details → room → hall → landing
    if (window.history.state?.afae === "painting") unwind();
    else if (window.location.pathname.startsWith("/p/")) window.history.replaceState({ afae: "hall" }, "", "/");
  };
  // details → "Next painting ›" / "‹ Previous" / "next feeling ›": back in the room, gliding on one painting at a time
  const roomList = (p) => window.__afae?.rooms?.()[p.section] || paintings.filter((q) => q.section === p.section).map((q) => q.slug);
  // while a painting plays or its details are open, the next one in the room is fetched in idle time only, never competing with a tap
  useEffect(() => {
    if (!active || !["immersion", "details"].includes(stage)) return;
    const list = roomList(active);
    const next = paintings.find((q) => q.slug === list[list.indexOf(active.slug) + 1]);
    if (!next) return;
    const idle = (fn) => (window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 4000 }) : setTimeout(fn, 1200));
    const unidle = (h) => (window.cancelIdleCallback ? window.cancelIdleCallback(h) : clearTimeout(h));
    let h = null;
    // details: only warm the HTTP cache for the next image and music (no decode, no upload)
    if (stage === "details") {
      h = idle(() => prefetchJourney(next, false, true));
      return () => unidle(h);
    }
    // a journey: its textures only once the current journey is well under way (never during its start)
    const id = setInterval(() => {
      if ((window.__afaeArc?.t || 0) < 10) return;
      clearInterval(id);
      h = idle(() => prefetchJourney(next, hasWebGL()));
    }, 2000);
    return () => {
      clearInterval(id);
      if (h) unidle(h);
    };
  }, [active?.id, stage]); // eslint-disable-line react-hooks/exhaustive-deps
  const detailsNav = (p) => {
    const list = roomList(p);
    const i = list.indexOf(p.slug);
    const nextRoom = RAINBOW_ORDER[(RAINBOW_ORDER.indexOf(p.section) + 1) % RAINBOW_ORDER.length];
    return { prev: i > 0 ? list[i - 1] : null, next: i >= 0 && i < list.length - 1 ? list[i + 1] : null, nextRoom, nextName: feelingName(nextRoom) };
  };
  const stepFromDetails = (slug, room) => {
    mark("nextTap", ["glideFirst", "glideEnd", "stepCall", "frame1", "unpaused"]);
    const in3d = mode === "3d";
    // the glide's clock starts at the tap itself, before any of the details view's teardown
    if (in3d) (mark("stepCall"), slug ? window.__afae?.stepToSlug(slug) : window.__afae?.glide(room));
    back();
  };
  const toLanding = () => setVeil("rise");
  // the brand tag is the way home from anywhere: a journey stops cleanly (its scene unmounts and frees WebGL, the music crossfades), then the landing
  const goHome = () => {
    resetRefs();
    if (stage) {
      setActive(null);
      setStage(null);
      audio.setScene("gallery");
      if (window.history.state?.afae === "painting") unwind();
    }
    setLeaving(false);
    setSeated(false);
    setRevealing(false);
    window.history.replaceState({ afae: "hall" }, "", "/");
    toLanding();
  };
  const landed = () => {
    setEntered(false);
    setLeaving(false);
    setSeated(false);
    setRevealing(false);
  };
  // "Gallery": straight to the Feeling Wall from anywhere (a journey stops cleanly: its scene unmounts and frees WebGL, the music crossfades)
  const toGallery = () => {
    resetRefs();
    if (stage) {
      setActive(null);
      setStage(null);
      audio.setScene("gallery");
      if (window.history.state?.afae === "painting") unwind();
      else window.history.replaceState({ afae: "hall" }, "", "/");
    }
    if (mode === "3d") {
      score("hall", undefined, hallUrl);
      setTimeout(() => window.__afae?.glide("hall"), 80);
    } else {
      setView2d("wall");
      score("hall", undefined, hallUrl);
    }
  };
  nav.current = { back, stand, toLanding, enter, toProjection };
  // explicit navigation: details → projection (same painting), or straight back to the room at painting k; history is only replaced, never pushed on the way out
  const goBack = () => {
    const cur = placeRef.current;
    if (cur === "painting") {
      if (stageRef.current !== "immersion" && !fromWindow.current && !(fromRoom.current && journeyDone.current)) return toProjection();
      return back();
    }
    if (window.history.state?.afae === cur || (cur === "hall" && window.history.state?.afae === "room")) return window.history.back();
    ({ bench: stand, hall: toLanding })[cur]?.();
  };
  // a room is its own history step: entering one from the hall pushes it, returning to the hall by the UI pops it
  useEffect(() => {
    const st = window.history.state?.afae;
    if (galleryRegion !== "hall" && st === "hall" && placeRef.current === "hall") push("room", "/");
    if (galleryRegion === "hall" && st === "room") unwind();
  }, [galleryRegion]);
  useEffect(() => {
    const onPop = (e) => {
      if (skipPop.current > 0) return void skipPop.current--;
      const to = e.state?.afae || "landing";
      const cur = placeRef.current;
      if (cur === "painting") return nav.current.back();
      if (cur === "hall" && to === "hall" && regionRef.current !== "hall") return window.__afae?.glide("hall");
      if (cur === "hall" && to === "landing" && regionRef.current !== "hall") {
        push("hall", "/");
        return window.__afae?.glide("hall");
      }
      if (cur === "bench") return nav.current.stand();
      if (cur === "hall" && to === "landing") return nav.current.toLanding();
      if (cur === "landing" && to === "hall") return nav.current.enter(true);
    };
    window.addEventListener("popstate", onPop);
    window.__afaeNav = { place: () => placeRef.current, region: () => regionRef.current, history: () => window.history.state, zone: (id) => window.__afae?.toRoom(id) };
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const deepSlug = useMemo(() => decodeURIComponent((window.location.pathname.match(/^\/p\/([^/?#]+)/) || [])[1] || ""), []);
  const deepDone = useRef(false);
  useEffect(() => {
    if (!deepSlug || deepDone.current || !paintings.length || !settingsReady) return;
    deepDone.current = true;
    const p = paintings.find((q) => q.slug === deepSlug);
    if (!p) return window.history.replaceState(null, "", "/");
    window.history.replaceState({ afae: "landing" }, "", "/");
    push("hall", "/");
    standBefore(paintings, p.slug);
    setEntered(true);
    pending.current = p;
    startArrival();
    const unlock = () => {
      unlockAudio();
      audio.setScene(moodOf(p));
      window.removeEventListener("pointerdown", unlock, true);
      window.removeEventListener("keydown", unlock, true);
    };
    window.addEventListener("pointerdown", unlock, true);
    window.addEventListener("keydown", unlock, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepSlug, paintings, settingsReady]);
  const toggleMute = () => {
    const m = !muted;
    setMuted(m);
    sessionStorage.setItem(MUTE_KEY, m ? "1" : "0");
    audio.setMuted(m);
  };

  const Experience = active ? EXPERIENCES[active.experience_type] : null;
  const slow = mode === "2d" || slowDevice() || reducedMotion();
  const endArrival = (why) => {
    window.__afaeArrivalEnd = why;
    setArrival(false);
    const p = pending.current;
    pending.current = null;
    if (p) return open(p);
    score("hall", undefined, hallUrl);
    window.__afaeMusicResync?.();
    Object.assign(pose, FEEL_STAND, { pitch: -0.03 });
    window.__afaeSettle = performance.now();
  };

  return (
    <div className="app-root" data-testid="app-root">
      {entered && mode === "3d" && (
        <GLBoundary key={`glb-${galleryKey}`} onError={(e) => fallTo2D("gl-error", e?.message)}>
          <GalleryRoom key={`gallery-${galleryKey}`} liteStart={glLite} paintings={paintings} paused={!!active || !!stage || flipbook} idle={arrival === "rainbow" && arrivalHold} seated={seated} revealing={revealing} visited={viewed} onOpen={(p) => open(p, true)} onSpecial={openSpecial} onReveal={reveal} onRevealed={sit} onSwitch2D={() => (setView2d("grid"), setMode("2d"))} onFallback={onGalleryFallback} onMusic={onMusic} onRegion={setGalleryRegion} onMemory={onMemory} onBook={setNearBook} />
        </GLBoundary>
      )}
      {entered && mode === "2d" && view2d === "wall" && <Hall2D onDoor={(id) => (setRoom2d(id), setView2d("rooms"))} />}
      {entered && mode === "2d" && view2d === "rooms" && <Rooms2D startRoom={room2d} paintings={paintings} webgl={webgl} onOpen={open} onCatalogue={() => setView2d("grid")} onSwitch3D={() => setMode("3d")} />}
      {entered && mode === "2d" && view2d === "grid" && <FallbackGrid paintings={paintings} webgl={webgl} onOpen={open} onSwitch3D={() => setMode("3d")} />}
      <AnimatePresence>
        {!entered && <EntryScreen key="entry" paintings={paintings} poem={settings.poem} onEnter={enter} onFeeling={enterWith} onFirstTouch={unlockAudio} />}
      </AnimatePresence>
      {stage === "immersion" && active && (
        <>
          <Immersion key={`imm-${active.id}`} painting={active} onDone={toNext} onNext={fromRoom.current ? () => (detailsNav(active).next ? stepFromDetails(detailsNav(active).next) : stepFromDetails(null, detailsNav(active).nextRoom)) : null} nextLabel={detailsNav(active).next ? "Next painting ›" : "next feeling ›"} />
        </>
      )}
      {stage === "experience" && Experience && (
        <>
          <Experience key={active.id} painting={active} onDone={toDetails} />
          <SeeButton key={`see-${active.id}`} onSee={toDetails} />
        </>
      )}
      <AnimatePresence>{stage === "details" && active && <DetailsView key={active.id} painting={active} onBack={goBack} nav={detailsNav(active)} onStep={stepFromDetails} />}</AnimatePresence>
      
      {stage && active && <ReturnControl tone="ink" label={fromWindow.current ? "to the windows" : stage === "immersion" || (fromRoom.current && journeyDone.current) ? "to the room" : "to the projection room"} testid="painting-return-control" onClick={goBack} onGallery={toGallery} />}
      <AnimatePresence>{gather && <GatherBack key="gather" painting={gather.painting} target={gather.target} onDone={() => setGather(null)} />}</AnimatePresence>
      <AnimatePresence>
        {entered && !stage && !seated && !revealing && !tourEnd && !menuOpen && !flipbook && !nearBook && atMemory && !memoryWave && galleryRegion === "hall" && <SitInvite key="invite" onSit={reveal} onDismiss={() => setMemoryWave(true)} />}
      </AnimatePresence>
      <AnimatePresence>{seated && <SittingScene key="sit" paintings={paintings} backdrop={mode === "3d" ? "hall" : "dom"} onStand={goBack} />}</AnimatePresence>
      {entered && !stage && !seated && !revealing && !leaving && <div className={mode === "3d" ? "hidden sm:block" : ""}><LeaveButton onClick={() => setLeaving(true)} /></div>}
      {entered && !stage && !seated && !revealing && !leaving && !veil && !arrival && (mode === "3d" && galleryRegion !== "hall" ? <ReturnControl label="to the Feeling Wall" testid="room-return-control" onClick={() => window.__afae?.glide("hall")} /> : mode === "2d" && view2d !== "wall" ? <ReturnControl label="Gallery" testid="hall-gallery-control" onClick={toGallery} /> : null)}
      {veil && <BubbleVeil key={veil} mode={veil} onMidpoint={veil === "rise" ? landed : undefined} onDone={() => setVeil(null)} />}
      <AnimatePresence>{leaving && <JourneyCard key="journey" paintings={paintings} viewed={viewed} onClose={() => setLeaving(false)} onMemories={hasSat() ? undefined : toMemories} />}</AnimatePresence>
      <FlipbookOverlay onChange={setFlipbook} atMemory={atMemory} />
      <GalleryShare open={sharing} onClose={() => setSharing(false)} />
      <AnimatePresence>{opening && <OpeningLetter key="opening" note={opening.note} signature={opening.signature} onClose={closeOpening} />}</AnimatePresence>
      {(entered || audioOn) && <MuteButton muted={muted} onToggle={toggleMute} />}
      <HeartGuard getPainting={() => (stage && active ? active : stage ? null : window.__afaeFocus)} onDetails={() => stage === "details"} paintings={paintings} />
      {arrival === "rainbow" && <RainbowArrival simple={slow} gentle={reducedMotion()} paintings={paintings} onRelease={() => setArrivalHold(false)} onDone={(why) => (why === "next" ? setArrival("arch-rainbow") : endArrival(why))} />}
      {(arrival === "arch" || arrival === "arch-rainbow") && <Arrival simple={slow} rainbow={arrival === "arch-rainbow"} onDone={endArrival} />}
      <LetterForm paintings={paintings} />
      {stage === "immersion" && active && <HeartToggle painting={active} testid="heart-toggle-immersion" className="fixed bottom-6 left-6 z-[75]" />}
      {!stage && !seated && !arrival && <div className={entered && mode === "3d" ? "hidden sm:block" : ""}><Credits /></div>}
      <BrandBanner raised={seated || !entered || !!stage} onHome={entered ? goHome : undefined} />
      {perf.debug && <DebugTimer />}
      {!stage && !seated && !arrival && <RightsLine testid="rights-line-corner" className={`fixed bottom-3 right-24 z-[80] ${!entered ? "[@media(max-height:560px)]:hidden" : mode === "2d" ? "hidden sm:block [@media(max-height:560px)]:hidden" : "hidden sm:block"}`} />}
    </div>
  );
}
